import React from 'react';
import { notificationService } from './notificationService';
import { WS_CONFIG } from '../config/apiConfig';

export interface WebSocketMessage {
  type: string;
  payload: any;
  timestamp: number;
}

export interface WebSocketOptions {
  url: string;
  protocols?: string | string[];
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  heartbeatInterval?: number;
  debug?: boolean;
}

export type WebSocketEventHandler = (data: any) => void;

export class WebSocketService {
  private ws: WebSocket | null = null;
  private options: Required<WebSocketOptions>;
  private eventHandlers = new Map<string, WebSocketEventHandler[]>();
  private reconnectAttempts = 0;
  private reconnectTimeoutId: NodeJS.Timeout | null = null;
  private heartbeatIntervalId: NodeJS.Timeout | null = null;
  private isIntentionallyClosed = false;

  constructor(options: WebSocketOptions) {
    this.options = {
      protocols: [],
      reconnectInterval: 3000,
      maxReconnectAttempts: 5,
      heartbeatInterval: 30000,
      debug: false,
      ...options,
    };
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.isIntentionallyClosed = false;
        
        // Add auth token to URL if available
        const token = localStorage.getItem('adminToken');
        const url = token 
          ? `${this.options.url}?token=${encodeURIComponent(token)}`
          : this.options.url;

        this.ws = new WebSocket(url, this.options.protocols);

        this.ws.onopen = () => {
          this.log('WebSocket connected');
          this.reconnectAttempts = 0;
          this.startHeartbeat();
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event);
        };

        this.ws.onclose = (event) => {
          this.log('WebSocket disconnected', event.code, event.reason);
          this.stopHeartbeat();
          
          if (!this.isIntentionallyClosed) {
            this.handleReconnect();
          }
        };

        this.ws.onerror = (error) => {
          this.log('WebSocket error', error);
          reject(error);
        };

        // Connection timeout
        setTimeout(() => {
          if (this.ws?.readyState === WebSocket.CONNECTING) {
            this.ws.close();
            reject(new Error('WebSocket connection timeout'));
          }
        }, 10000);

      } catch (error) {
        reject(error);
      }
    });
  }

  disconnect(): void {
    this.isIntentionallyClosed = true;
    
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    
    this.stopHeartbeat();
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.log('WebSocket manually disconnected');
  }

  send(type: string, payload: any): boolean {
    if (!this.isConnected()) {
      this.log('Cannot send message: WebSocket not connected');
      return false;
    }

    const message: WebSocketMessage = {
      type,
      payload,
      timestamp: Date.now(),
    };

    try {
      this.ws!.send(JSON.stringify(message));
      this.log('Message sent', message);
      return true;
    } catch (error) {
      this.log('Failed to send message', error);
      return false;
    }
  }

  on(eventType: string, handler: WebSocketEventHandler): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType)!.push(handler);
  }

  off(eventType: string, handler?: WebSocketEventHandler): void {
    if (!this.eventHandlers.has(eventType)) {
      return;
    }

    if (!handler) {
      this.eventHandlers.delete(eventType);
      return;
    }

    const handlers = this.eventHandlers.get(eventType)!;
    const index = handlers.indexOf(handler);
    if (index !== -1) {
      handlers.splice(index, 1);
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getReadyState(): number | null {
    return this.ws?.readyState || null;
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);
      this.log('Message received', message);

      // Handle system messages
      if (message.type === 'ping') {
        this.send('pong', { timestamp: Date.now() });
        return;
      }

      // Emit to event handlers
      const handlers = this.eventHandlers.get(message.type);
      if (handlers) {
        handlers.forEach(handler => {
          try {
            handler(message.payload);
          } catch (error) {
            console.error('Error in WebSocket event handler:', error);
          }
        });
      }

      // Emit to general message handlers
      const generalHandlers = this.eventHandlers.get('message');
      if (generalHandlers) {
        generalHandlers.forEach(handler => {
          try {
            handler(message);
          } catch (error) {
            console.error('Error in general WebSocket handler:', error);
          }
        });
      }

    } catch (error) {
      this.log('Failed to parse WebSocket message', error);
    }
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.options.maxReconnectAttempts) {
      this.log('Max reconnection attempts reached');
      notificationService.error({
        title: 'Connection Lost',
        description: 'Unable to maintain real-time connection. Please refresh the page.',
        duration: 0, // Don't auto-close
      });
      return;
    }

    this.reconnectAttempts++;
    this.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.options.maxReconnectAttempts})`);

    this.reconnectTimeoutId = setTimeout(() => {
      this.connect().catch(error => {
        this.log('Reconnection failed', error);
      });
    }, this.options.reconnectInterval);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    
    this.heartbeatIntervalId = setInterval(() => {
      if (this.isConnected()) {
        this.send('ping', { timestamp: Date.now() });
      }
    }, this.options.heartbeatInterval);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatIntervalId) {
      clearInterval(this.heartbeatIntervalId);
      this.heartbeatIntervalId = null;
    }
  }

  private log(...args: any[]): void {
    if (this.options.debug) {
      console.log('[WebSocket]', ...args);
    }
  }
}

// Global WebSocket instance
let globalWebSocket: WebSocketService | null = null;

export function initializeWebSocket(options: WebSocketOptions): WebSocketService {
  if (globalWebSocket) {
    globalWebSocket.disconnect();
  }
  
  globalWebSocket = new WebSocketService(options);
  return globalWebSocket;
}

export function getWebSocket(): WebSocketService | null {
  return globalWebSocket;
}

// Real-time event types
export const WebSocketEvents = {
  // User events
  USER_CREATED: 'user:created',
  USER_UPDATED: 'user:updated',
  USER_DELETED: 'user:deleted',
  USER_STATUS_CHANGED: 'user:status_changed',
  
  // System events
  SYSTEM_MAINTENANCE: 'system:maintenance',
  SYSTEM_NOTIFICATION: 'system:notification',
  
  // Admin events
  ADMIN_ACTION: 'admin:action',
  ADMIN_LOGIN: 'admin:login',
  
  // General events
  MESSAGE: 'message',
  ERROR: 'error',
  NOTIFICATION: 'notification',
} as const;

// WebSocket hook for React components
export function useWebSocket(eventType: string, handler: WebSocketEventHandler) {
  React.useEffect(() => {
    const ws = getWebSocket();
    if (ws) {
      ws.on(eventType, handler);
      
      return () => {
        ws.off(eventType, handler);
      };
    }
  }, [eventType, handler]);
}

// Auto-connect WebSocket when token is available
export function autoConnectWebSocket(): void {
  const token = localStorage.getItem('adminToken');
  if (token && !globalWebSocket?.isConnected()) {
    const ws = initializeWebSocket({
      url: WS_CONFIG.url,
      debug: WS_CONFIG.debug,
      reconnectInterval: WS_CONFIG.reconnectInterval,
      maxReconnectAttempts: WS_CONFIG.maxReconnectAttempts,
      heartbeatInterval: WS_CONFIG.heartbeatInterval,
    });

    ws.connect().catch(error => {
      console.error('Failed to auto-connect WebSocket:', error);
    });

    // Handle real-time notifications
    ws.on(WebSocketEvents.NOTIFICATION, (data) => {
      notificationService.info({
        title: data.title || 'Notification',
        description: data.message,
      });
    });

    // Handle system maintenance notifications
    ws.on(WebSocketEvents.SYSTEM_MAINTENANCE, (data) => {
      notificationService.warning({
        title: 'System Maintenance',
        description: data.message,
        duration: 0,
      });
    });
  }
}
